import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import {
  CrossReferenceImportReport,
  OttoFilterSearchLogEntry,
  OttoOwnCrossReference,
  SpringPage
} from '../models/otto-olsen-cross-reference';

@Injectable({ providedIn: 'root' })
export class OttoOlsenCrossReferenceAdminService {
  private baseUrl = 'json/otto/cross-reference/admin';

  // Same pattern as UsersService.getAllUsersSubject(): every mutation refetches and re-emits
  // the full list, so list configs subscribed to the subject re-render automatically.
  private crossReferencesSubject?: BehaviorSubject<OttoOwnCrossReference[] | null>;

  constructor(protected http: HttpClient) {}

  getCrossReferencesSubject(): Observable<OttoOwnCrossReference[] | null> {
    if (!this.crossReferencesSubject) {
      this.crossReferencesSubject = new BehaviorSubject<OttoOwnCrossReference[] | null>(null);
      this.updateCrossReferencesSubject();
    }
    return this.crossReferencesSubject.asObservable();
  }

  updateCrossReferencesSubject(): void {
    this.list().subscribe({
      next: page => this.crossReferencesSubject?.next(page.content),
      error: () => this.crossReferencesSubject?.next([])
    });
  }

  list(search = '', page = 0, size = 10000): Observable<SpringPage<OttoOwnCrossReference>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<SpringPage<OttoOwnCrossReference>>(this.baseUrl, { params });
  }

  save(ref: OttoOwnCrossReference): Observable<OttoOwnCrossReference> {
    return this.http.post<OttoOwnCrossReference>(this.baseUrl, ref).pipe(tap(() => this.updateCrossReferencesSubject()));
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(tap(() => this.updateCrossReferencesSubject()));
  }

  exportExcel(): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.baseUrl}/export`, { responseType: 'blob', observe: 'response' });
  }

  importExcel(file: File): Observable<CrossReferenceImportReport> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post<CrossReferenceImportReport>(`${this.baseUrl}/import`, formData)
      .pipe(tap(report => report.applied && this.updateCrossReferencesSubject()));
  }

  stats(from: string, to: string, page = 0, size = 50): Observable<SpringPage<OttoFilterSearchLogEntry>> {
    const params = new HttpParams().set('from', from).set('to', to).set('page', page).set('size', size);
    return this.http.get<SpringPage<OttoFilterSearchLogEntry>>(`${this.baseUrl}/stats`, { params });
  }

  statsExportExcel(from: string, to: string): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get(`${this.baseUrl}/stats/export`, { params, responseType: 'blob', observe: 'response' });
  }
}
